import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeyValue } from '@app/shared/interfaces/KeyValue.interface';
import { Clinic } from '@app/shared/interfaces/clinic.interface';
import { catchError, map, Observable, of } from 'rxjs';
import { UserAvailability } from '../interfaces/userAvailability.interface';
import { environment } from '@env/environment';
import { UserRegistrationRequest } from '../interfaces/userRegistrationRequest.interface';
import { Provider } from '@app/shared/interfaces/provider.interface';
import { CaseContact } from '@app/shared/interfaces/caseContact.interface';

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  constructor(private httpClient: HttpClient) {}

  getClinicList(): Observable<Clinic[]> {
    return this.httpClient.get<Clinic[]>(`${environment.apiUrl}/nextgen/clinics`)
      .pipe(
        map((clinics: Clinic[]) => this.sortClinicsByName(clinics)),
        catchError(() => of<Clinic[]>([]))
      );
  }

  getProviderList(): Observable<Provider[]> {
    return this.httpClient.get<Provider[]>(`${environment.apiUrl}/nextgen/providers`)
      .pipe(
        map((providers: Provider[]) => this.sortByLastName(providers)),
        catchError(() => of<Provider[]>([]))
      );
  }

  getCaseContactList(searchTerm?: string): Observable<CaseContact[]> {
    const endpointUrl = (searchTerm ?? '').trim().length > 0 ? `${environment.apiUrl}/nextgen/case-contacts/search?searchTerm=${searchTerm}` : `${environment.apiUrl}/nextgen/case-contacts`
    return this.httpClient.get<CaseContact[]>(endpointUrl)
      .pipe(
        map((contacts: CaseContact[]) => this.sortCaseContactsByLastName(contacts)),
        catchError(() => of<CaseContact[]>([]))
      );
  }

  checkEmailAvailability(email: string): Observable<UserAvailability> {
    return this.httpClient.post<UserAvailability>
    (`${environment.apiUrl}/registration/check-username-email-availability`, { email });
  }

  registerUser(userRegistrationRequest: UserRegistrationRequest): Observable<any> {
    if (userRegistrationRequest.accountTypeLookupId !== 4) {
      return this.httpClient.post(`${environment.apiUrl}/registration/work-comp-user/register`, userRegistrationRequest);
    } else {
      return this.httpClient.post(`${environment.apiUrl}/registration/referral-user/register`, userRegistrationRequest);
    }
  }

  getCaseContact$(selectedCaseContactId: string): Observable<CaseContact> {
    return this.httpClient.get<CaseContact>(`${environment.apiUrl}/nextgen/case-contacts/${selectedCaseContactId}`);
  }

  private sortClinicsByName(options: Clinic[]): Clinic[] {
    return [...options]
      .sort((a: Clinic, b: Clinic) =>
        (a.name ?? '').localeCompare((b.name ?? ''), undefined, { numeric: true, sensitivity: 'base' }));
  }

  private sortByLastName(options: Provider[]): Provider[] {
    return [...options]
      .sort((a: Provider, b: Provider) => {
        return a.lastName.localeCompare(b.lastName, undefined, { numeric: true, sensitivity: 'base' });
      });
  }

  private sortCaseContactsByLastName(options: CaseContact[]): CaseContact[] {
    return [...options]
      .sort((a: CaseContact, b: CaseContact) => {
        return (a.lastName ?? '').localeCompare((b.lastName ?? ''), undefined, { numeric: true, sensitivity: 'base' });
      });
  }

}
